import { Option } from '@sapphire/result';

import {
  classifySurvey,
  resolveFirstProjectChildId,
  resolveTrackerWaveId,
  SURVEY_CLASSIFICATION_KIND,
  SURVEY_CLASSIFICATION_ROLE,
  Survey,
  SurveyKind,
  SurveyLocaleData,
} from '@ps/survey-utils';
import { compareByField, compareByLocale, Nullable } from '@ps/utils';

import {
  DEFAULT_CORE_CHILD_STATE,
  DEFAULT_SURVEY_CHILD_KEY,
} from '../../survey-creation-state.constants';
import {
  SurveyChildRegistryState,
  SurveyCreationCoreState,
} from '../../survey-creation-state.model';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import {
  buildLocaleKeyedChildren,
  buildWaveKeyedChildren,
  ChildMapPatch,
  patchChildrenMap,
  resolveActiveChildKey,
  SurveyChildScope,
} from '../../survey-creation-child-registry.helper';

/**
 * A fresh core state targeting `rootSurveyId`. Edit mode (a root id is
 * already known) registers the one child under that id directly instead of
 * the empty-string pre-draft placeholder, so the survey load can key off it
 * immediately instead of waiting on a `selectChild`/pre-draft rekey.
 */
export function initializeCoreState(
  rootSurveyId: Nullable<number>,
): SurveyCreationCoreState {
  const childKey = rootSurveyId ?? DEFAULT_SURVEY_CHILD_KEY;

  return {
    rootSurveyId,
    activeChildKey: null,
    kind: null,
    // Every caller of this function carries `trackerConfig` forward itself
    // (see the `init` handler in the core reducer) — this is only the
    // fallback for a genuinely fresh state, where there's nothing to carry.
    trackerConfig: null,
    children: {
      [childKey]: DEFAULT_CORE_CHILD_STATE,
    },
  };
}

/**
 * On loading the root survey for edit: keeps an already-chosen child,
 * otherwise targets the project's first child, falling back to the root
 * survey itself for a standard (non-project) survey.
 */
export function resolveLoadedActiveChildKey(
  activeChildKey: Nullable<SurveyChildKey>,
  survey: Survey,
): SurveyChildKey {
  return (
    activeChildKey ?? resolveFirstProjectChildId(survey).unwrapOr(survey.id)
  );
}

/**
 * On loading the root survey for edit: whether it's part of a family at all.
 * A standalone role means no family — except `MC` (traditional multi-country),
 * whose surveys are always `role: standalone` despite having siblings, since
 * classic MC has no parent/child hierarchy, only peers linked by `mcUniqueId`.
 */
export function resolveLoadedParticipant(survey: Survey): Nullable<boolean> {
  const { kind, role } = classifySurvey(survey);

  if (kind === SURVEY_CLASSIFICATION_KIND.STANDARD) {
    return null;
  }

  return role !== SURVEY_CLASSIFICATION_ROLE.PARENT;
}

/**
 * The single child a per-child *read* should resolve to. Falls back to the
 * registry's first key so callers never have to null-check a missing entry.
 */
export function resolveActiveCoreChildKey(
  state: SurveyCreationCoreState,
): SurveyChildKey {
  return resolveActiveChildKey(
    state.children,
    state.activeChildKey,
    DEFAULT_SURVEY_CHILD_KEY,
  );
}

/**
 * The child to highlight once the family has loaded. `mc.surveys`/tracker
 * waves arrive in the API's own order while the drawer sorts its rows by
 * locale (MC) or wave number (Tracker), so the two can disagree on which
 * child comes first. Only the load-time fallback is re-pointed; a child the
 * buyer picked is left alone.
 */
export function resolveDisplayedActiveChildKey(
  state: SurveyCreationCoreState,
  surveys: readonly Survey[],
): Nullable<SurveyChildKey> {
  const rootSurvey = Option.from(state.rootSurveyId)
    .map((rootSurveyId) => state.children[rootSurveyId]?.survey)
    .andThen(Option.from);

  const isLoadFallback = rootSurvey
    .andThen(resolveFirstProjectChildId)
    .mapOr(
      !state.activeChildKey,
      (fallback) => fallback === state.activeChildKey,
    );

  if (!isLoadFallback) {
    return state.activeChildKey;
  }

  const isTracker = rootSurvey.mapOr(false, SurveyKind.isTracker);

  return Option.from(
    surveys
      .filter(resolveLoadedParticipant)
      .sort(
        isTracker ? compareByField(resolveTrackerWaveId) : compareByLocale,
      )[0],
  ).mapOr(state.activeChildKey, ({ id }) => id);
}

export function resolveReTargetedActiveChildKey(
  state: SurveyCreationCoreState,
  surveyId: number,
): Nullable<SurveyChildKey> {
  const survey = state.children[surveyId]?.survey;

  return survey && SurveyKind.isProjectParent(survey)
    ? state.activeChildKey
    : surveyId;
}

/**
 * Whether a save response describes the child that was being edited. A Group MC
 * answers with the project row, and adopting that into the active locale would
 * hand it the project's id and the family's summed goal.
 */
export function describesActiveCoreChild(
  state: SurveyCreationCoreState,
  survey: Survey,
): boolean {
  const child = state.children[resolveActiveCoreChildKey(state)];

  return !child?.survey || child.survey.id === survey.id;
}

/**
 * Whether the family is already in the registry and this survey is one of it.
 * A locale switch re-`init`s with a sibling's id: `init` keeps the registry
 * instead of resetting, and the load effect skips the fetch.
 */
export function isLoadedCoreSibling(
  children: Record<SurveyChildKey, SurveyChildRegistryState>,
  surveyId: number,
): boolean {
  return !!children[surveyId] && Object.keys(children).length > 1;
}

export function activeProjectChildKey(
  survey: Survey,
  activeChildKey: Nullable<SurveyChildKey>,
): Option<number> {
  return SurveyKind.isProject(survey) &&
    typeof activeChildKey === 'number' &&
    activeChildKey !== survey.id
    ? Option.some(activeChildKey)
    : Option.none;
}

export function rootSurveyOf(core: SurveyCreationCoreState): Option<Survey> {
  return Option.from(core.rootSurveyId).andThen((surveyId) =>
    Option.from(core.children[surveyId]?.survey),
  );
}

export type CoreChildPatch = ChildMapPatch<SurveyChildRegistryState>;

export function patchCoreChildren(
  state: SurveyCreationCoreState,
  scope: SurveyChildScope,
  patch: CoreChildPatch,
): SurveyCreationCoreState {
  return {
    ...state,
    children: patchChildrenMap(
      state.children,
      state.activeChildKey,
      scope,
      patch,
      DEFAULT_CORE_CHILD_STATE,
    ),
  };
}

/**
 * Tracker is wave-keyed ("1".."N", via `buildWaveKeyedChildren`) — there's no
 * "sibling locale" concept to normalize against, every wave starts selected
 * (matches legacy's `checked: true` default).
 */
export function buildTrackerPreDraftCoreChildren(
  children: Record<SurveyChildKey, SurveyChildRegistryState>,
  numberOfWaves: number,
): Record<SurveyChildKey, SurveyChildRegistryState> {
  return buildWaveKeyedChildren(children, numberOfWaves, () => ({
    ...DEFAULT_CORE_CHILD_STATE,
    participant: true,
  }));
}

/**
 * Pre-draft only: uses the shared `buildLocaleKeyedChildren` mechanic so the
 * registry gains the same locale keys as `audience`'s own pre-draft
 * children, then runs through `normalizeCoreParticipant`, whose single-child
 * rule is specifically about MC's not-yet-split-into-locales case.
 */
export function buildMcPreDraftCoreChildren(
  children: Record<SurveyChildKey, SurveyChildRegistryState>,
  localeData: SurveyLocaleData[],
): Record<SurveyChildKey, SurveyChildRegistryState> {
  const nextChildren = buildLocaleKeyedChildren(
    children,
    localeData,
    DEFAULT_SURVEY_CHILD_KEY,
    () => DEFAULT_CORE_CHILD_STATE,
  );

  return normalizeCoreParticipant(nextChildren);
}

/**
 * `participant` means "one of several sibling locales" — meaningless with
 * only one child, so a lone survivor is normalized back to `null` instead of
 * staying selected. Once a second locale exists, every child needs an
 * explicit true/false, defaulting new ones to selected (`true`).
 */
function normalizeCoreParticipant(
  children: Record<SurveyChildKey, SurveyChildRegistryState>,
): Record<SurveyChildKey, SurveyChildRegistryState> {
  const keys = Object.keys(children);

  if (keys.length === 1) {
    const [key] = keys;
    return { [key]: { ...children[key], participant: null } };
  }

  const normalizedChildren: Record<SurveyChildKey, SurveyChildRegistryState> =
    {};

  for (const key of keys) {
    normalizedChildren[key] = {
      ...children[key],
      participant: children[key].participant ?? true,
    };
  }

  return normalizedChildren;
}
